// @flow

import React, { useState } from 'react';
import classnames from 'classnames';

import { t } from 'Src/i18n';
import { useRouter } from 'Client/router/useRouter';
import { type TrackingPayload, useTracking } from 'Tracking/tracking-api/useTracking';
import { TrackClick } from 'Tracking/track-click/TrackClick';
import { type Destination } from 'Components/lines/destinations-line/DestinationsLine.utils';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import Image from 'Components/frameworks/image/Image';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Price } from 'Components/frameworks/price/Price';
import { ProductPriceModal } from 'Components/widgets/product-price-modal/ProductPriceModal';
import { createProductUrlWithParams } from 'Components/widgets/product/Product.mapper';
import { generateUrlWithParams } from 'Utils/uri/uriUtils';

type Props = {
  ...$Exact<Destination>,
  productType: string,
  trackingData: {
    ...TrackingPayload,
    labelGA: string,
  },
};

export const DestinationCard = (props: Props) => {
  const [isHover, setHover] = useState(false);
  const [priceDetailIsDisplayed, setPriceDetailIsDisplayed] = useState(false);

  const { id, label, link, mediaUrl, price, productType, trackingData } = props;

  const { history, location } = useRouter<{}, *>();

  const track = useTracking(trackingData);

  const handleClick = (e: SyntheticEvent<*>) => {
    e.preventDefault(); // Prevent event from propagating to card link
    track({
      labelGA: `${trackingData.labelGA}_bestPrice`,
    });
    setPriceDetailIsDisplayed(!priceDetailIsDisplayed);
  };

  const handleFocus = () => {
    setHover(true);
  };

  const handleBlur = () => {
    setHover(false);
  };

  return (
    <article
      className="bg-gray-darker image-responsive-cell rounded-tiny"
      data-testid="destination-card"
    >
      <div
        className="block overflow-hidden relative"
        onMouseEnter={handleFocus}
        onMouseLeave={handleBlur}
        onFocus={handleFocus}
        onBlur={handleBlur}
        data-testid={`destination-card-${id}-link`}
      >
        <Image
          altMsg={label}
          className={classnames('transition-transform-opacity opacity-75', {
            'scale-105': isHover,
          })}
          imageUrl={mediaUrl}
          groupKey="destinations"
          ratioKey={ratioUtils.R_38_BY_28}
          hasLazyLoading
        />
        <div className="transition-transform">
          <TrackClick as={DynamicLink} trackingData={trackingData} url={link}>
            <div className="absolute inset-0" />
            <h3 className="w-full absolute p-2 pb-16 bottom-0 reset-heading font-bold leading-tight text-xl text-center text-white capitalize">
              {label}
            </h3>
          </TrackClick>
          <Price
            onClick={handleClick}
            bestPrice={price.bestPrice}
            className="justify-center bottom-0 absolute p-8 p-2 capitalize"
            containerStyle={{ minHeight: '1.5rem' }}
            from={t('price.format.from')}
            theme="instagram"
            unit={price.unit}
          />
          <ProductPriceModal
            isVisible={priceDetailIsDisplayed}
            setIsVisible={setPriceDetailIsDisplayed}
            price={price}
            type={productType}
            onClick={e => {
              e.preventDefault();
              setPriceDetailIsDisplayed(false);
              track({
                labelGA: `${trackingData.labelGA}_discoverResort`,
              });
              if (price.productHref) {
                const productUrl = generateUrlWithParams(price.productHref, {
                  queryParams: location.queryParams,
                });
                history.pushRoute(createProductUrlWithParams(productUrl, productType));
              }
            }}
          />
        </div>
      </div>
    </article>
  );
};
